<ng-container *transloco="let t; read: 'configuration'">
<div id="news" class="p-5">
  <div class="flex">
    <drdp-title [title]="t('news.title')" class="pb-5"></drdp-title>
    @if (canManageNewsItem) {
      <a
        class="text-drdpblue-300 mt-2 pl-5"
        routerLink="/manage-news"
        >{{ t('news.manageNews') }}
        <i class="uil uil-arrow-circle-right"></i>
      </a>
    }
  </div>
  <drdp-container additionClasses="px-10 py-10">
    <form (ngSubmit)="submitForm()">
      <ng-container [formGroup]="newsItemForm">
        <div
          class="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-6"
          >
          <div
            class="lg:col-span-4 md:col-span-3 sm:col-span-2 col-span-1"
            *drdpCheckPermission="Permission.CreateAllAgencyNewsItem"
            >
            <mat-checkbox
              class="rounded-checkboxes"
              color="primary"
              [checked]="isGlobal.value"
              formControlName="isGlobal"
              (change)="handleGlobal()"
              [disabled]="id > 0"
              >
            </mat-checkbox>
            <label class="inline">
              {{ t('news.applyToAllAgencies') }}
            </label>
          </div>

          <div
            class="lg:col-span-4 md:col-span-3 sm:col-span-2 col-span-1"
            >
            <mat-checkbox
              class="rounded-checkboxes"
              color="primary"
              [checked]="isStateLevel.value"
              formControlName="isStateLevel"
              (change)="handleStateLevel()"
              [disabled]="id > 0"
              >
            </mat-checkbox>
            <label class="inline">
              {{ t('news.stateLevelNewsItem') }}
            </label>
          </div>

          <drdp-filter-select
            #newsTypeSelect
            [label]="t('news.newsItemType')"
            [multiSelect]="false"
            [options]="newsItemTypes"
            [selectType]="selectNewsItemTypes"
            [initialValues]="newsItemTypeInitialValue"
            [required]="true"
            (onSelected)="handleType($event)"
            >
          </drdp-filter-select>

          <div id="role">
            <label class="required mb-0"> {{ t('news.role') }} </label>
            <mat-form-field class="fixBorder" appearance="fill">
              <mat-select
                formControlName="roleIds"
                [placeholder]="t('news.selectRoles')"
                multiple
                >
                @for (role of roles; track role) {
                  <!-- Server-supplied role name: localized by the API, not by Transloco. -->
                  <mat-option [value]="role.id">
                    {{ role.roleName }}
                  </mat-option>
                }
              </mat-select>
              @if (!roleIds.valid) {
                <mat-error>{{ required }}</mat-error>
              }
            </mat-form-field>
          </div>

          <drdp-select-state
            [hidden]="isGlobal.value"
            #stateSelect
            [initialStateIds]="stateInitialValues"
            [multiSelect]="true"
            (state)="handleState($event)"
            [clear]="clearDropdowns"
            [required]="true">
          </drdp-select-state>

          <drdp-select-agency
            [hidden]="isGlobal.value || isStateLevel.value"
            #agencySelect
            (agency)="handleAgency($event)"
            [stateId]="stateIds.value"
            [clear]="clearDropdowns"
            ngClass="h-full z-40"
            [required]="true"
            [multiSelect]="true"
            [multiSelectInitVals]="true"
            [initialAgencyIds]="agencyInitialValues"
            >
          </drdp-select-agency>

          <drdp-select-site
            #siteSelect
            [hidden]="isGlobal.value || isStateLevel.value"
            (site)="handleSites($event)"
            [agencyId]="agencyIds.value"
            [clear]="clearDropdowns"
            [multiSelect]="true"
            [required]="true"
            [initialSiteIds]="siteInitialValues"
            >
          </drdp-select-site>

          <div class="relative">
            <drdp-date-picker
              [label]="t('common.startDate')"
              [initialDate]="newsItemDate?.value"
              [isRequired]="true"
              (emitDate)="handleDate($event, 'newsItemDate')"
              >
            </drdp-date-picker>
            <div class="absolute top-0 left-full mt-1 ml-[-2px]">
              <i class="uil uil-info-circle text-xs" [matTooltip]=dateInfo></i>
            </div>
          </div>

          <div class="relative">
            <drdp-date-picker
              [label]="t('common.endDate')"
              [initialDate]="newsItemEndDate?.value"
              [isRequired]="true"
              [resetDate]="resetDate"
              (emitDate)="handleDate($event, 'newsItemEndDate')"
              >
            </drdp-date-picker>
            <div class="absolute top-0 left-full mt-1 ml-[-2px]">
              <i class="uil uil-info-circle text-xs" [matTooltip]=dateInfo></i>
            </div>
            @if (newsItemForm.hasError('dateMismatch')) {
              <mat-error>
                {{ t('common.invalidDateRange') }}</mat-error
                >
              }
              @if (newsItemForm.hasError('sameDate')) {
                <mat-error>
                  {{ t('common.sameDateError') }}
                </mat-error>
              }
            </div>
            <div class="lg:col-span-3 md:col-span-3 sm:col-span-2 col-span-1">
              <label class="font-normal required" for="newsItemTitle"
                >{{ t('news.articleTitle') }}</label
                >
                <mat-form-field class="bg-white" appearance="fill">
                  <input
                    matInput
                    [placeholder]="t('news.articleTitle')"
                    autocomplete="off"
                    id="newsItemTitle"
                    type="text"
                    formControlName="newsItemTitle"
                    maxlength="100"
                    required
                    />
                  @if (!newsItemTitle.valid) {
                    <mat-error>{{ required }}</mat-error>
                  }
                </mat-form-field>
              </div>
              <div class="lg:col-span-4 md:col-span-3 sm:col-span-2 col-span-1">
                <label class="pt-3 font-formal required" for="newsItem"
                  >{{ t('news.articleBody') }}</label
                  >
                  <mat-form-field class="bg-white" appearance="outline">
                    <textarea
                      matInput
                      [placeholder]="t('news.articleBody')"
                      autocomplete="off"
                      id="newsItem"
                      formControlName="newsItem"
                      cdkTextareaAutosize
                      cdkAutosizeMinRows="10"
                      maxlength="376"
                      required
                      class="border border-solid border-black"
                      >
                    </textarea>
                    @if (!newsItem.valid) {
                      <mat-error>{{ required }}</mat-error>
                    }
                  </mat-form-field>
                </div>
              </div>
              <div class="mt-10 mb-2 flex justify-end">
                <drdp-btn
                  class="pr-4"
                  type="button"
                  (click)="resetForm()"
                  color="cancel"
                  >
                  {{ t('news.cancelReset') }}
                </drdp-btn>
                <drdp-btn
                  type="submit"
                  [disabled]="!newsItemForm.valid"
                  cdkFocusInitial
                  >
                  {{ t('news.saveSubmit') }}
                </drdp-btn>
              </div>
            </ng-container>
          </form>
        </drdp-container>
      </div>
</ng-container>
